import React, { useState } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { s_PB, s_GC } from '../../shared/styles';
import { t } from '../../shared/i18n';
import { EXERCISES } from '../exercises/constants';

// Map agenda exercise types to relevant EXERCISES entries
const EXERCISE_PICKS = {
  sport:      ["yoga","stretching","powerpose"],
  walk:       ["walking","stretching","breathing"],
  default:    ["walking","yoga","stretching"],
};
// Map agenda rest types to relevant EXERCISES entries
const REST_PICKS = {
  rest:       ["breathing","bodyscan","gratitude"],
  breathing:  ["breathing","bodyscan","meditation"],
  meditation: ["meditation","gratitude","bodyscan"],
  default:    ["breathing","meditation","bodyscan"],
};

function pickExercises(ids) {
  return ids.map(id => EXERCISES.find(e => e.id === id)).filter(Boolean);
}

export default function WeekSuggestionsScreen({ lang, weekAgenda, setWeekAgenda, onDone }) {
  const nl = lang === 'nl';
  const days = ["mon","tue","wed","thu","fri","sat","sun"].map(d=>t(`day.${d}`,lang));
  const [expanded, setExpanded] = useState(null);
  const [added, setAdded] = useState({});

  // Collect unique exercise-type days and rest-type days from the agenda
  const exerciseDays = [];
  const restDays = [];
  for (let i = 0; i < 7; i++) {
    const items = weekAgenda[i] || [];
    const exItem  = items.find(a => a.type === 'exercise');
    const rstItem = items.find(a => a.type === 'rest');
    if (exItem)  exerciseDays.push({ dayIdx: i, agendaId: exItem.id,  icon: exItem.icon,  labelNL: exItem.labelNL, label: exItem.label });
    if (rstItem) restDays.push(   { dayIdx: i, agendaId: rstItem.id, icon: rstItem.icon, labelNL: rstItem.labelNL, label: rstItem.label });
  }

  // Deduplicate: only one representative per agendaId
  const uniqueExTypes = [...new Map(exerciseDays.map(d => [d.agendaId, d])).values()];
  const uniqueRstTypes = [...new Map(restDays.map(d => [d.agendaId, d])).values()];

  // Suggested exercises per type
  const exSuggestions = uniqueExTypes.length
    ? pickExercises(EXERCISE_PICKS[uniqueExTypes[0].agendaId] || EXERCISE_PICKS.default)
    : pickExercises(EXERCISE_PICKS.default);
  const rstSuggestions = uniqueRstTypes.length
    ? pickExercises(REST_PICKS[uniqueRstTypes[0].agendaId] || REST_PICKS.default)
    : pickExercises(REST_PICKS.default);

  // Which days are exercise vs rest
  const exDayLabels = exerciseDays.map(d => days[d.dayIdx]);
  const rstDayLabels = restDays.map(d => days[d.dayIdx]);

  // Add exercise to all days of a given type
  const addToAgenda = (ex, dayIndices) => {
    if (!setWeekAgenda) return;
    setWeekAgenda(prev => {
      const next = { ...prev };
      dayIndices.forEach(i => {
        const existing = next[i] || [];
        if (existing.some(a => a.id === ex.id)) return;
        next[i] = [...existing, { id: ex.id, type: 'exercise', label: ex.labelEN, labelNL: ex.label, icon: ex.icon }];
      });
      return next;
    });
    setAdded(prev => ({ ...prev, [ex.id]: true }));
  };

  const ExCard = ({ ex, accent, dayIndices }) => {
    const open = expanded === ex.id;
    return (
      <button
        onClick={() => setExpanded(open ? null : ex.id)}
        style={{
          width: '100%', textAlign: 'left', cursor: 'pointer',
          background: open ? `${accent}14` : _T.bg3,
          borderTop: `1.5px solid ${open ? accent : _T.cardBorderTop}`,
          borderBottom: `1px solid ${_T.cardBorderBot}`,
          borderLeft: `1px solid ${open ? accent + '55' : 'rgba(255,255,255,0.03)'}`,
          borderRight: '1px solid rgba(255,255,255,0.03)',
          borderRadius: 14, padding: '12px 14px', marginBottom: 6,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, flexShrink: 0,
            background: `${accent}20`, border: `1px solid ${accent}44`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
          }}>{ex.icon}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, color: C.white, fontWeight: 600 }}>
              {nl ? ex.label : ex.labelEN}
            </div>
            <div style={{ fontSize: 11, color: C.silver, marginTop: 2 }}>
              {ex.duration} min · {nl ? ex.desc : ex.descEN}
            </div>
          </div>
          <div style={{ fontSize: 12, color: accent }}>{open ? '▲' : '▼'}</div>
        </div>
        {open && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${accent}22` }}>
            <div style={{ fontSize: 13, color: C.silver2, lineHeight: 1.6 }}>
              {nl ? ex.desc : ex.descEN}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
              {(nl ? ex.choices : ex.choicesEN).map((c, i) => (
                <span key={i} style={{
                  fontSize: 10, padding: '3px 9px', borderRadius: 8,
                  background: `${accent}18`, border: `1px solid ${accent}44`, color: accent,
                }}>{c}</span>
              ))}
            </div>
            {dayIndices.length > 0 && (
              <button
                onClick={e => { e.stopPropagation(); addToAgenda(ex, dayIndices); }}
                style={{
                  marginTop: 10, width: '100%', padding: '8px 12px', borderRadius: 10, cursor: 'pointer',
                  background: added[ex.id] ? `${accent}22` : accent,
                  border: `1px solid ${accent}`,
                  color: added[ex.id] ? accent : '#0a1220',
                  fontSize: 12, fontWeight: 700,
                }}
              >
                {added[ex.id]
                  ? t('suggest.added', lang)
                  : `${t('suggest.add_btn', lang)} (${dayIndices.map(i => days[i]).join(', ')})`}
              </button>
            )}
          </div>
        )}
      </button>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(160deg,${_T.bg},${_T.bg2})`,
      display: 'flex', flexDirection: 'column',
      padding: '24px 24px 32px', paddingTop: 52,
    }}>
      <style>{CSS}</style>

      {/* Header */}
      <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 600, marginBottom: 6 }}>
        {t('suggest.label', lang)}
      </div>
      <div style={{ fontFamily: 'Georgia,serif', fontSize: 22, color: C.white, marginBottom: 6, lineHeight: 1.3 }}>
        {t('suggest.title', lang)}
      </div>
      <div style={{ color: C.silver, fontSize: 13, marginBottom: 24, lineHeight: 1.6 }}>
        {t('suggest.subtitle', lang)}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 20 }}>

        {/* ── Exercise suggestions ── */}
        {exSuggestions.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8,
                background: `${_T.accent}20`, border: `1px solid ${_T.accent}44`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15,
              }}>🏃</div>
              <div>
                <div style={{ fontSize: 13, color: C.white, fontWeight: 700 }}>
                  {t('suggest.for_days', lang)}
                </div>
                {exDayLabels.length > 0 && (
                  <div style={{ fontSize: 11, color: _T.accent, marginTop: 1 }}>
                    {exDayLabels.join(' · ')}
                  </div>
                )}
              </div>
            </div>
            {exSuggestions.map(ex => <ExCard key={ex.id} ex={ex} accent={_T.accent} dayIndices={exerciseDays.map(d => d.dayIdx)} />)}
          </div>
        )}

        {/* ── Rest suggestions ── */}
        {rstSuggestions.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8,
                background: `${C.teal}18`, border: `1px solid ${C.teal}44`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15,
              }}>🌿</div>
              <div>
                <div style={{ fontSize: 13, color: C.white, fontWeight: 700 }}>
                  {t('suggest.rest_title', lang)}
                </div>
                {rstDayLabels.length > 0 && (
                  <div style={{ fontSize: 11, color: C.teal, marginTop: 1 }}>
                    {rstDayLabels.join(' · ')}
                  </div>
                )}
              </div>
            </div>
            {rstSuggestions.map(ex => <ExCard key={ex.id} ex={ex} accent={C.teal} dayIndices={restDays.map(d => d.dayIdx)} />)}
          </div>
        )}

        {/* Fallback: no agenda set */}
        {exSuggestions.length === 0 && rstSuggestions.length === 0 && (
          <div style={{ color: C.silver, fontSize: 14, textAlign: 'center', marginTop: 40 }}>
            {t('suggest.no_plan', lang)}
          </div>
        )}

        {/* Tip footer */}
        <div style={{
          ...s_GC({ padding: '14px 16px' }),
          borderLeft: `3px solid ${_T.accent}66`,
        }}>
          <div style={{ fontSize: 11, color: _T.accent, fontWeight: 600, marginBottom: 4 }}>
            💡 {nl ? 'TIP' : 'TIP'}
          </div>
          <div style={{ fontSize: 12, color: C.silver, lineHeight: 1.6 }}>
            {t('suggest.tip_text', lang)}
          </div>
        </div>
      </div>

      <button style={s_PB({ width: '100%', fontSize: 15, padding: '15px', marginTop: 16 })} onClick={onDone}>
        {t('suggest.continue', lang)}
      </button>
      <div style={{display:"flex",justifyContent:"center",gap:20,paddingTop:16}}>
        <a href="/privacy" style={{color:"#3a4a62",fontSize:11,textDecoration:"none"}}>Privacy Policy</a>
        <a href="/terms" style={{color:"#3a4a62",fontSize:11,textDecoration:"none"}}>Terms &amp; Conditions</a>
      </div>
    </div>
  );
}
